/* eslint-disable indent */
import { yupResolver } from '@hookform/resolvers/yup';
import './style.scss';

import { FileInput, TextareaInput, TextInput } from 'components/FormControl';
import { TOAST } from 'constant';
import { productDescriptionSchema } from 'features/Project/validation';
import { toast } from 'layout';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import PropTypes from 'prop-types';

import { forwardRef, useImperativeHandle, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatFileNameProduct } from 'utils/func';
import {
  useMutationCreateRegisterResultProduct,
  useMutationUpdateRegisterResultProduct,
} from 'utils/hooks/registerResult';

const ProductDialog = forwardRef(({ onSubmit, registerResultId, refetch }, ref) => {
  // #region Data
  const { t } = useTranslation();

  const [productControl, setProductControl] = useState();
  const [visible, setVisible] = useState(false);
  const { mutateAsync, isLoading } = useMutationCreateRegisterResultProduct();
  const { mutateAsync: mutateAsyncUpdate, isLoading: isLoadingUpdate } =
    useMutationUpdateRegisterResultProduct();

  const {
    control,
    getValues,
    reset,
    trigger,
    formState: { errors, isDirty, dirtyFields },
  } = useForm({ mode: 'onChange', resolver: yupResolver(productDescriptionSchema) });
  // #endregion Data

  // #region Event
  const showError = (message) => {
    toast(TOAST.ERROR, message);
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (_productControl) => {
        setProductControl(_productControl);
        setVisible(true);
        if (_productControl.type === 'INSERT') {
          reset({});
        } else if (_productControl.type === 'UPDATE') {
          reset(_productControl.value);
        }
      },
    }),
    []
  );

  const handleAddHardProduct = async () => {
    const isValidTrigger = await trigger();
    if (!isValidTrigger) {
      showError(t('errorMessage.validationErrorMessage'));
      return;
    }

    const data = getValues();

    const { insert, update, index, type } = productControl;

    if (type === 'INSERT' && insert) {
      insert(index, data);
      let id = null;
      if (!registerResultId) {
        id = await onSubmit(data);
      } else {
        id = registerResultId;
        await onSubmit(data);
      }
      if (id) {
        const dataSender = new FormData();
        Object.keys(data).forEach((key) => {
          dataSender.append(key, data[key]);
        });
        try {
          await mutateAsync({ registerResultId: id, body: dataSender });
          toast(TOAST.SUCCESS, t('Thêm sản phẩm thành công'));
        } catch (error) {
          toast(TOAST.ERROR, t('Thêm sản phẩm thất bại'));
        }
      }
      refetch();
    } else if (type === 'UPDATE' && update) {
      const dataSender = new FormData();
      Object.keys(data).forEach((key) => {
        if (['id', 'customId', 'createdAt', 'updatedAt', 'deletedAt'].includes(key)) return;
        dataSender.append(key, data[key]);
      });
      try {
        await mutateAsyncUpdate({ registerResultId, productId: data.id, body: dataSender });
        toast(TOAST.SUCCESS, t('Cập nhật sản phẩm thành công'));
      } catch (error) {
        toast(TOAST.ERROR, t('Cập nhật sản phẩm thất bại'));
      }
      refetch();
    }
    reset({});
    setVisible(false);
  };

  // #endregion Event

  const handleDownloadFile = () => {};

  return (
    <Dialog
      header={
        productControl?.type === 'INSERT'
          ? t('registerResult.registerContent.productDescription.dialog.addProduct')
          : t('registerResult.registerContent.productDescription.dialog.updateProduct')
      }
      visible={visible}
      onHide={() => {
        setVisible(false);
      }}
      draggable={false}
      className="w-full sm:w-7 md:w-6 lg:w-5"
    >
      <div className="grid p-fluid register-result-product-dialog">
        <div className="col-12">
          <TextInput
            label={t('registerResult.registerContent.productDescription.fields.productName')}
            name="name"
            control={control}
            errors={errors}
            isRequired
          />
        </div>
        <div className="col-12">
          <TextareaInput
            label={t('registerResult.registerContent.productDescription.fields.productDescription')}
            name="description"
            control={control}
            errors={errors}
            row={3}
          />
        </div>
        <div className="col-4">
          <TextInput
            label={t('registerResult.registerContent.productDescription.fields.productCapacity')}
            name="productionCapacity"
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-8">
          <TextInput
            label={t(
              'registerResult.registerContent.productDescription.fields.productImplementation'
            )}
            name="implementationLevel"
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-12">
          <TextInput
            label={t('registerResult.registerContent.productDescription.fields.productEconomy')}
            name="economicData"
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-12">
          <TextInput
            label={t(
              'registerResult.registerContent.productDescription.fields.productTransmission'
            )}
            name="transferMethod"
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-12">
          <TextInput
            label={t(
              'registerResult.registerContent.productDescription.fields.productDocumentType'
            )}
            name="documentType"
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-12">
          <TextareaInput
            label={t('registerResult.registerContent.productDescription.fields.productAddress')}
            name="address"
            control={control}
            errors={errors}
          />
        </div>
        <div className="col-12">
          <FileInput
            name="file"
            label={t('submitDocument.product.evidenceFile')}
            control={control}
            errors={errors}
            accept=".pdf"
            maxFileSize={30000000}
            isRequired
          />
          {productControl?.value?.fileUrl ? (
            <span
              className="p-button p-button-text p-button-sm mt-1 p-0"
              onClick={
                () =>
                  handleDownloadFile(
                    productControl?.value?.fileUrl,
                    formatFileNameProduct(
                      productControl?.value?.fileUrl || '',
                      productControl?.projectCode,
                      'a',
                      !productControl?.index ? 1 : parseInt(productControl?.index, 10) + 1
                    )
                  )
                // eslint-disable-next-line react/jsx-curly-newline
              }
            >
              <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
              <span className="text-blue-500 underline font-italic ml-1">
                {formatFileNameProduct(
                  productControl?.value?.fileUrl || '',
                  productControl?.projectCode,
                  'a',
                  !productControl?.index ? 1 : parseInt(productControl?.index, 10) + 1
                )}
              </span>
            </span>
          ) : (
            <span>
              {productControl?.value?.file?.name &&
                formatFileNameProduct(
                  productControl?.value?.file?.name || '',
                  productControl?.projectCode,
                  'a',
                  !productControl?.index ? 1 : parseInt(productControl?.index, 10) + 1
                )}
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-content-end mt-4">
        <Button
          label={t('formLayout.action.cancel')}
          severity="danger"
          onClick={() => {
            setVisible(false);
          }}
          className="mr-1 w-8rem"
        />
        <Button
          label={
            productControl?.type === 'INSERT'
              ? t('formLayout.action.add')
              : t('formLayout.action.update')
          }
          type="submit"
          severity="info"
          onClick={handleAddHardProduct}
          className="w-8rem"
          disabled={!(isDirty && Object.keys(dirtyFields).length) || isLoading || isLoadingUpdate}
        />
      </div>
    </Dialog>
  );
});

ProductDialog.propTypes = {
  onSubmit: PropTypes.func,
  registerResultId: PropTypes.string,
  refetch: PropTypes.func,
};

ProductDialog.defaultProps = {
  onSubmit: () => {},
  registerResultId: null,
  refetch: () => null,
};

export default ProductDialog;
